import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { startWith } from 'rxjs';
import { AuthService } from '../../services/auth';
import { ToastService } from '../../services/toast';

@Component({
  selector: 'app-login',
  templateUrl: './login.html',
  styleUrls: ['./login.css'],
  imports: [
    ReactiveFormsModule,
    MatIconModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Login {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private loginOtpTimer: ReturnType<typeof setInterval> | null = null;

  otpSent = signal(false);
  isSubmitting = signal(false);
  errorMessage = signal<string | null>(null);
  submittedIdentifier = signal('');
  loginOtpCountdown = signal(0);

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.clearOtpTimer();
    });
  }

  loginForm = this.fb.group({
    identifier: ['', [Validators.required, Login.mobileOrEmailValidator]],
  });

  otpForm = this.fb.group({
    otp: this.fb.array(
      Array.from({ length: 6 }, () =>
        this.fb.control('', [Validators.required, Validators.pattern(/^\d$/)]),
      ),
    ),
  });

  private readonly loginFormTick = toSignal(this.loginForm.valueChanges.pipe(startWith(null)), {
    initialValue: null,
  });

  private readonly otpFormTick = toSignal(this.otpForm.valueChanges.pipe(startWith(null)), {
    initialValue: null,
  });

  canSubmit = computed(() => {
    this.loginFormTick();
    this.otpFormTick();
    const activeForm = this.otpSent() ? this.otpForm : this.loginForm;
    return activeForm.valid && !this.isSubmitting();
  });

  onSendOtp(): void {
    this.errorMessage.set(null);

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    const identifier = this.loginForm.controls.identifier.value ?? '';
    this.auth.requestOtp(identifier, 'SIGN_IN').subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.auth.markOtpRequested(identifier, 'SIGN_IN');
        this.submittedIdentifier.set(identifier.trim());
        this.otpSent.set(true);
        this.startOtpCountdown();
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting.set(false);
        this.toast.error(this.extractApiError(error));
      },
    });
  }

  onVerifyOtp(): void {
    this.errorMessage.set(null);

    if (this.otpForm.invalid) {
      this.otpForm.markAllAsTouched();
      return;
    }

    const identifier = this.submittedIdentifier();
    const otp = this.otpForm.controls.otp.getRawValue().join('');
    this.isSubmitting.set(true);
    this.auth.verifyOtp(identifier, otp).subscribe({
      next: (response) => {
        this.isSubmitting.set(false);
        this.clearOtpTimer();

        if (response.data.staff.businessId == null) {
          this.auth.completeOtpVerification(identifier, response.data.staff);
          void this.router.navigate(['/onboarding'], {
            queryParams: {
              staffId: response.data.staff.staffId,
              isOnboarding: true,
            },
          });
        } else if (response.data.business?.erpDomainName?.trim()) {
          this.openDomainAndReset(
            response.data.business.erpDomainName,
            response.data.staff.staffId,
            response.data.staff.token ?? '',
          );
        } else {
          this.auth.completeOtpVerification(identifier, response.data.staff);
          this.resetLoginScreen();
          this.toast.success('Login successful.');
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting.set(false);
        this.toast.error(this.extractApiError(error));
      },
    });
  }

  editIdentifier(): void {
    this.otpSent.set(false);
    this.clearOtpTimer();
    this.otpForm.reset();
    this.errorMessage.set(null);
  }

  resendOtp(): void {
    if (this.loginOtpCountdown() > 0 || this.isSubmitting()) {
      return;
    }

    const identifier = this.submittedIdentifier();
    this.isSubmitting.set(true);
    this.auth.requestOtp(identifier, 'SIGN_IN').subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.auth.markOtpRequested(identifier, 'SIGN_IN');
        this.errorMessage.set(null);
        this.startOtpCountdown();
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting.set(false);
        this.toast.error(this.extractApiError(error));
      },
    });
  }

  formatOtpCountdown(seconds: number): string {
    return `0:${seconds.toString().padStart(2, '0')}`;
  }

  onOtpInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);
    const controls = this.loginOtpControls;
    controls.at(index).setValue(value);
    input.value = value;

    if (value && index < controls.length - 1) {
      this.focusOtpInput(index + 1);
    }
  }

  onOtpKeydown(index: number, event: KeyboardEvent): void {
    const controls = this.loginOtpControls;

    if (event.key === 'Backspace' && !controls.at(index).value && index > 0) {
      this.focusOtpInput(index - 1);
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    const digits = event.clipboardData?.getData('text').replace(/\D/g, '').slice(0, 6) ?? '';
    if (!digits) {
      return;
    }

    event.preventDefault();
    const controls = this.loginOtpControls;
    controls.controls.forEach((control, index) => control.setValue(digits[index] ?? ''));
    this.focusOtpInput(Math.min(digits.length, controls.length) - 1);
  }

  get loginOtpControls(): FormArray {
    return this.otpForm.controls.otp;
  }

  private focusOtpInput(index: number): void {
    const input = document.getElementById(`login-otp-${index}`) as HTMLInputElement | null;
    input?.focus();
  }

  private startOtpCountdown(): void {
    this.clearOtpTimer();
    this.loginOtpCountdown.set(60);

    this.loginOtpTimer = setInterval(() => {
      const remaining = this.loginOtpCountdown();
      if (remaining <= 1) {
        this.loginOtpCountdown.set(0);
        this.clearOtpTimer();
        return;
      }
      this.loginOtpCountdown.set(remaining - 1);
    }, 1000);
  }

  private clearOtpTimer(): void {
    if (this.loginOtpTimer) {
      clearInterval(this.loginOtpTimer);
      this.loginOtpTimer = null;
    }
  }

  private extractApiError(error: HttpErrorResponse): string {
    const body = error.error;
    if (body && typeof body === 'object') {
      const message = body.message ?? body.error ?? body.errorMessage;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (error.status === 0) {
      return 'Unable to reach the server. Please check your connection and try again.';
    }
    return 'Something went wrong. Please try again.';
  }

  private openDomainAndReset(
    domain: string,
    staffId: string,
    token: string,
  ): void {
    const domainUrl = /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;
    const url = new URL(domainUrl);
    url.searchParams.set('staffId', staffId);
    url.searchParams.set('isOnboarding', 'true');
    if (token) {
      url.searchParams.set('token', token);
    }
    const targetUrl = url.toString();

    const opened = window.open(targetUrl, '_blank');
    if (!opened) {
      this.toast.error('Please allow pop-ups to open the business portal.');
      return;
    }

    try {
      opened.opener = null;
    } catch {
      // The dashboard is already opening in its isolated tab.
    }

    this.resetLoginScreen();
  }

  private resetLoginScreen(): void {
    this.auth.logout();
    this.clearOtpTimer();
    this.loginOtpCountdown.set(0);

    this.otpSent.set(false);
    this.isSubmitting.set(false);
    this.errorMessage.set(null);
    this.submittedIdentifier.set('');

    this.loginForm.reset({ identifier: '' });
    this.resetOtpBoxes(this.loginOtpControls);
  }

  private resetOtpBoxes(controls: FormArray): void {
    controls.controls.forEach((control) => {
      control.reset('');
      control.markAsUntouched();
      control.markAsPristine();
    });
  }

  private static mobileOrEmailValidator(
    control: AbstractControl<string>,
  ): ValidationErrors | null {
    const value = control.value.trim();
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    const isMobile = /^\d{10}$/.test(value);

    return isEmail || isMobile ? null : { mobileOrEmail: true };
  }
}
